import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { loginApi, meApi } from 'api/auth';
import {
  AUTH_STORAGE_KEYS,
  clearAuthSession,
  clearPendingTwoFactorToken as clearStoredPendingTwoFactorToken,
  clearSessionReauthToken as clearStoredSessionReauthToken,
  dispatchSessionRestoredEvent,
  readPendingTwoFactorToken,
  readSessionReauthToken,
  readStoredAuthSession,
  setAuthSession,
  setLogoutSuppressReauth,
  setPendingTwoFactorToken as storePendingTwoFactorToken,
  setSessionReauthToken as storeSessionReauthToken
} from 'utils/authSession';

// ==============================|| AUTH CONTEXT ||============================== //

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth debe ser usado dentro de AuthProvider');
  }
  return context;
};

const logAuthError = (context, err) => {
  const status = err?.status ?? err?.response?.status ?? null;
  const message = err?.data?.message || err?.message || 'AUTH_ERROR';
  console.error(`[AUTH] ${context}`, { status, message });
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [pendingTwoFactorToken, setPendingTwoFactorTokenState] = useState(() => readPendingTwoFactorToken());
  const [sessionReauthToken, setSessionReauthTokenState] = useState(() => readSessionReauthToken());
  const rememberSessionRef = useRef(false);

  const setPendingTwoFactorToken = useCallback((token) => {
    setPendingTwoFactorTokenState(token);
    storePendingTwoFactorToken(token);
  }, []);

  const setSessionReauthToken = useCallback((token) => {
    setSessionReauthTokenState(token);
    storeSessionReauthToken(token);
  }, []);

  const clearPendingTwoFactorToken = useCallback(() => {
    setPendingTwoFactorTokenState(null);
    clearStoredPendingTwoFactorToken();
  }, []);

  const clearSessionReauth = useCallback(() => {
    setSessionReauthTokenState(null);
    clearStoredSessionReauthToken();
  }, []);

  const clearPendingAuthState = useCallback(() => {
    clearPendingTwoFactorToken();
    clearSessionReauth();
  }, [clearPendingTwoFactorToken, clearSessionReauth]);

  // Verificar token guardado y obtener /auth/me
  useEffect(() => {
    const storedSession = readStoredAuthSession();
    const token = storedSession?.token || null;
    const savedUser = storedSession?.user || null;
    const pendingSessionReauth = readSessionReauthToken();

    if (pendingSessionReauth) {
      setSessionReauthToken(pendingSessionReauth);
    }
    rememberSessionRef.current = Boolean(storedSession?.persistSession);

    async function bootstrap() {
      if (!token || pendingSessionReauth) {
        if (savedUser) {
          setUser(savedUser);
          setIsAuthenticated(true);
        } else if (pendingSessionReauth) {
          setIsAuthenticated(true);
        }
        setLoading(false);
        return;
      }

      try {
        const me = await meApi();
        setUser(me.user);
        setIsAuthenticated(true);
      } catch (err) {
        const twoFactorExpired = err?.data?.message === 'TWO_FACTOR_EXPIRED';
        logAuthError('bootstrap session validation failed', err);

        if (twoFactorExpired) {
          const storedPending = err?.data?.pendingToken || readSessionReauthToken() || token;
          setSessionReauthToken(storedPending || null);
          if (savedUser) {
            setUser(savedUser);
            setIsAuthenticated(true);
          }
        } else {
          clearAuthSession();
          setIsAuthenticated(false);
          setUser(null);
          rememberSessionRef.current = false;
        }
      }

      setLoading(false);
    }

    // Cargar dato basico del user para evitar parpadeos
    if (savedUser) {
      setUser(savedUser);
      setIsAuthenticated(true);
    }

    bootstrap();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setSession = useCallback((token, userPayload, options = {}) => {
    const persistSession = typeof options.persistSession === 'boolean' ? options.persistSession : rememberSessionRef.current;
    rememberSessionRef.current = Boolean(persistSession);
    setAuthSession({ token, user: userPayload, persistSession });
    setLogoutSuppressReauth(false);
    setUser(userPayload);
    setIsAuthenticated(true);
    clearPendingAuthState();
    dispatchSessionRestoredEvent();
  }, [clearPendingAuthState]);

  useEffect(() => {
    const handler = (event) => {
      const pendingToken = event?.detail?.pendingToken || readPendingTwoFactorToken();
      setPendingTwoFactorToken(pendingToken || null);
    };

    window.addEventListener('vrp:two-factor-required', handler);
    return () => window.removeEventListener('vrp:two-factor-required', handler);
  }, [setPendingTwoFactorToken]);

  useEffect(() => {
    const handler = (event) => {
      const pendingToken = event?.detail?.pendingToken || readSessionReauthToken();
      setSessionReauthToken(pendingToken || null);
    };

    window.addEventListener('vrp:session-expired', handler);
    return () => window.removeEventListener('vrp:session-expired', handler);
  }, [setSessionReauthToken]);

  useEffect(() => {
    const handleStorage = (event) => {
      if (event.key === AUTH_STORAGE_KEYS.PENDING_SESSION_REAUTH_KEY) {
        setSessionReauthTokenState(event.newValue || null);
      }
      if (event.key === AUTH_STORAGE_KEYS.PENDING_TWO_FACTOR_KEY) {
        setPendingTwoFactorTokenState(event.newValue || null);
      }
    };

    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, []);

  const login = async (username, password, options = {}) => {
    try {
      const persistSession = Boolean(options.persistSession);
      rememberSessionRef.current = persistSession;
      const { token, user } = await loginApi(username, password);
      setSession(token, user, { persistSession });
      return { success: true, user };
    } catch (err) {
      const message = typeof err?.data === 'object' && err.data?.message ? err.data.message : err.message || 'Error de autenticacion';
      const pendingToken = err?.data?.pendingToken;
      if (err?.data?.message === 'TWO_FACTOR_REQUIRED' && pendingToken) {
        setPendingTwoFactorToken(pendingToken);
        return {
          success: false,
          error: message,
          code: err.data.message,
          pendingToken
        };
      }
      return {
        success: false,
        error: message,
        code: err?.data?.message,
        status: err?.status
      };
    }
  };

  const logout = () => {
    setLogoutSuppressReauth(true);
    setUser(null);
    setIsAuthenticated(false);
    clearAuthSession();
    clearPendingAuthState();
    rememberSessionRef.current = false;
  };

  const value = {
    user,
    isAuthenticated,
    loading,
    login,
    setSession,
    logout,
    pendingTwoFactorToken,
    setPendingTwoFactorToken,
    clearPendingTwoFactorToken,
    sessionReauthToken,
    setSessionReauthToken,
    clearSessionReauth,
    clearPendingAuthState
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthContext;
